import {api} from 'api';
import ApiService from 'api/ApiService';
import {Msg} from 'components/notifications/NotificationContext';
import * as openpgp from 'openpgp';
import {buildPath} from 'utils';

export async function uploadStreamChunks(
   file: File,
   publicKeyArmored: string | null,
   refetchFolder: () => Promise<void>,
   path: string,
   encrypt: boolean = true
): Promise<void> {
   console.debug('Upload', path, encrypt);
   const toastId = Msg.notify(`Uploading ${file.name}: 0%`, 'info', {autoClose: false});

   try {
      let stream: ReadableStream<Uint8Array>;
      let targetFileName = file.name;

      if (encrypt) {
         if (!publicKeyArmored) {
            throw new Error('Public key is required for encryption');
         }

         const publicKey = await openpgp.readKey({armoredKey: publicKeyArmored});

         const message = await openpgp.createMessage({
            binary: file.stream(),
            format: 'binary',
            filename: file.name,
         });

         // Encrypt file stream using OpenPGP
         stream = await openpgp.encrypt({
            message,
            encryptionKeys: publicKey,
            format: 'binary',
            config: {
               preferredCompressionAlgorithm: openpgp.enums.compression.zlib,
               allowMissingKeyFlags: true,
            },
         });

         // Append .pgp extension only for encrypted files
         targetFileName = file.name + '.pgp';

         console.log(`Starting encrypted upload: ${file.name}`);
      } else {
         // Use original file stream without encryption
         stream = file.stream();
         console.log(`Starting plain upload: ${file.name}`);
      }

      const reader = stream.getReader();
      const chunkSize = 100 * 1024 * 1024; // 100 MB per chunk

      let buffer = new Uint8Array(0);
      let chunkIndex = 0;
      let uploadedBytes = 0;

      while (true) {
         const {value, done} = await reader.read();

         if (value) {
            // Append new data to internal buffer
            const tmp = new Uint8Array(buffer.length + value.length);
            tmp.set(buffer);
            tmp.set(value, buffer.length);
            buffer = tmp;
         }

         // Send chunks while buffer contains enough data
         while (buffer.length >= chunkSize || (done && buffer.length > 0)) {
            const toSend = buffer.slice(0, chunkSize);
            buffer = buffer.slice(chunkSize);

            try {
               await ApiService.postSecureJson(api.upload, toSend, {
                  headers: {
                     'X-File-Path': encodeURIComponent(buildPath(path, targetFileName)),
                     // First chunk creates file, next chunks append
                     'X-Append-File': chunkIndex === 0 ? 'false' : 'true',
                     // Mark last chunk for server-side finalization
                     'X-Last-Chunk': done && buffer.length === 0 ? 'true' : 'false',
                  },
               });
            } catch (res) {
               const text = await (res as Response).text().catch(() => '');
               throw new Error(
                  `Chunk ${chunkIndex} upload failed: ${(res as Response).statusText} ${text}`
               );
            }

            uploadedBytes += toSend.length;

            // Progress is approximate when encryption is enabled
            const progress = Math.min(100, Math.round((uploadedBytes / file.size) * 100));

            Msg.update(toastId, {
               render: `Uploading ${file.name}: ${progress}%`,
            });

            ++chunkIndex;
         }

         if (done) break;
      }

      Msg.update(toastId, {
         render: `Upload completed: ${file.name}`,
         type: 'success',
         autoClose: 3000,
      });

      await refetchFolder();
   } catch (err) {
      Msg.update(toastId, {
         render: `Error: ${(err as Error).message}`,
         type: 'error',
         autoClose: 5000,
      });

      console.error(err);
   }
}
